import React, { useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { LocationOn } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Stack,
} from '@mui/material';

import CalendarFilter from './components/CalendarFilter';
import DetailsModal from './components/DetailsModal';
import EventCarousel from './components/EventCarousel';
import EventInfo from './components/EventInfo';
import OngoingEvent from './components/OngoingEvent';
import { useEvents } from './hooks/useEvents';
import { useNextEvents, Event } from './hooks/useNextEvents';
import Spin from 'components/Spin';
import Text from 'components/Text';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import GlobalModal from 'modals/GlobalModal/GlobalModal';
import { useIsMobile, useIsTablet } from 'utils/mediaQueries';

import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

const AgendaPage: React.FC = () => {
  const theme = useThemeContext();
  const { rawEvents, events, refreshEvents, loading, error } = useEvents();
  const {
    nextEvents,
    loading: loadingNextEvents,
    error: errorNextEvents,
    refreshNextEvents,
  } = useNextEvents();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [isOngoingEvent, setIsOngoingEvent] = useState(true);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const navigate = useNavigate();
  const routes = useMenuRoutes();

  // Função para encontrar a rota da sala baseada no roomId
  const findRoomRoute = (roomId: number): string | null => {
    const roomRoute = routes.find(
      route => route.isMeetingRoom && route.path.includes(`/${roomId}`)
    );
    return roomRoute?.path || null;
  };

  // Funções para ações dos eventos em andamento
  const handleEventDetails = (event: Event) => {
    console.log('Ver detalhes do evento:', event);
    setSelectedEvent(event);
    setIsDetailsModalOpen(true);
  };

  const handleAccessMeeting = (event: Event) => {
    console.log('Acessar reunião:', event);
    if (event.isOnline && event.roomId) {
      const roomPath = findRoomRoute(event.roomId);
      if (roomPath) {
        console.log('Redirecionando para sala virtual:', roomPath);
        navigate(roomPath);
      } else {
        console.warn('Rota da sala não encontrada para roomId:', event.roomId);
        // Fallback: mostrar modal com informações da sala
        alert(`Sala virtual não encontrada. ID da sala: ${event.roomId}`);
      }
    } else {
      // Para eventos presenciais, mostrar modal com informações de localização
      console.log('Evento presencial:', event.location);
      setSelectedEvent(event);
      setIsLocationModalOpen(true);
    }
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedEvent(null);
  };

  const handleCloseLocationModal = () => {
    setIsLocationModalOpen(false);
    setSelectedEvent(null);
  };

  const eventDates = events.map(event => dayjs(event.date, 'DD/MM/YYYY').format('YYYY-MM-DD'));

  const filteredEvents = events.filter(event =>
    dayjs(event.date, 'DD/MM/YYYY').isSame(selectedDate, 'day')
  );

  const selectedDateFormatted = selectedDate
    ? dayjs(selectedDate).format('DD [de] MMMM [de] YYYY')
    : '';

  if (loading || loadingNextEvents) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', padding: 0 }}>
        <Spin />
      </Container>
    );
  }

  if (error) {
    console.error(error);
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <Text color="gray">Erro ao carregar eventos. Tente novamente mais tarde.</Text>
      </Container>
    );
  }

  if (errorNextEvents) {
    console.error(errorNextEvents);
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <Text color="gray">Erro ao carregar próximos eventos. Tente novamente mais tarde.</Text>
      </Container>
    );
  }

  // Filtra apenas eventos que estão acontecendo agora
  const ongoingEvents = nextEvents.filter(event => event.isOngoing);

  console.log('[AGENDA PAGE] ongoingEvents', ongoingEvents);

  return (
    <>
      <Container
        sx={{
          marginBottom: '5rem',
          padding: isMobile ? 0.5 : 'auto',
          background:
            isMobile || isTablet ? theme.background.secondary : theme.background.secondary,

          borderRadius: isMobile || isTablet ? 2 : 0,
        }}
      >
        {/* <Box
        display="flex"
        maxWidth={isMobile || isTablet ? '100%' : '25%'}
        gap="0"
        mb="1rem"
        padding={isMobile || isTablet ? 3 : 0}
      >
        <Button
          size="small"
          variant={tab === 'MEMBER' ? 'primary' : 'outline'}
          onClick={() => setTab('MEMBER')}
          fullWidth
          style={{ borderRadius: '8px 0px 0px 8px' }}
          disabled
        >
          Membro
        </Button>
        <Button
          size="small"
          variant={tab === 'COMMUNITY' ? 'primary' : 'outline'}
          onClick={() => setTab('COMMUNITY')}
          fullWidth
          style={{ borderRadius: '0px 8px 8px 0px' }}
        >
          Comunidade
        </Button>
      </Box> */}
        <Box>
          {nextEvents.length > 0 ? (
            <>
              <Box sx={{ paddingBottom: isMobile ? 1 : 0 }}>
                <Text size="medium" bold>
                  Próximos Eventos
                </Text>
              </Box>
              <EventCarousel
                events={rawEvents}
                nextEvents={nextEvents}
                refreshEvents={refreshEvents}
                refreshNextEvents={refreshNextEvents}
              />
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: isMobile ? '2rem' : '3rem',
              }}
            >
              <Text size="medium" color="textSecondary">
                Nenhum evento encontrado.
              </Text>
            </Box>
          )}
        </Box>

        <Box
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          sx={{
            background: theme.background.primary,
            borderRadius: 2,
            padding: isMobile || isTablet ? -2 : 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'center',
              padding: isMobile || isTablet ? 0 : 2.5,
              flex: isMobile ? 1 : 0.5,
            }}
          >
            <CalendarFilter
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              eventDates={eventDates}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              paddingTop: isMobile ? '2rem' : '2rem',
              paddingRight: isMobile || isTablet ? 0 : 0.5,
              flex: isMobile ? 1 : 0.5,
            }}
          >
            <Box sx={{ marginBottom: 0.5, padding: isMobile || isTablet ? 1 : 0 }}>
              <Text color="primary" bold>
                <Text size="medium" bold color="primary">
                  {selectedDateFormatted}
                </Text>
              </Text>
            </Box>
            <Box
              sx={{
                padding: isMobile || isTablet ? 1 : 0,
              }}
            >
              {filteredEvents.length > 0 ? (
                filteredEvents.map(event => (
                  <Accordion
                    key={event.id}
                    sx={{
                      color: theme.text.primary,
                      marginBottom: 0.75,
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.primary }} />}
                      sx={{
                        background: theme.background.secondary,
                        borderRadius: 2,
                        //padding: isMobile ? "0.5rem" : "1rem",
                      }}
                    >
                      <Text size="small" bold>
                        {event.title}
                      </Text>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        background: theme.background.secondary,
                        //padding: isMobile ? "0.5rem" : "1rem",
                      }}
                    >
                      <Text size="small">{event.location}</Text>
                      {event.participants.participants.some(
                        participant => participant.participant_role.code === 'COORDINATOR'
                      ) && (
                        <Text size="small">
                          Coordenador:{' '}
                          {
                            event.participants.participants.find(
                              participant => participant.participant_role.code === 'COORDINATOR'
                            )?.first_name
                          }{' '}
                          {
                            event.participants.participants.find(
                              participant => participant.participant_role.code === 'COORDINATOR'
                            )?.last_name
                          }
                        </Text>
                      )}

                      {event.participants.participants.some(
                        participant => participant.participant_role.code === 'PRESENTER'
                      ) && (
                        <Text size="small">
                          Palestrantes:{' '}
                          {event.participants.participants
                            .filter(
                              participant => participant.participant_role.code === 'PRESENTER'
                            )
                            .map(presenter => `${presenter.first_name} ${presenter.last_name}`)
                            .join(', ')}
                        </Text>
                      )}

                      <EventInfo
                        totalParticipants={event.participants.total}
                        duration={event.duration}
                        time={event.time}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: isMobile ? '2rem' : '3rem',
                  }}
                >
                  <Text size="medium" color="textSecondary">
                    Nenhum evento encontrado para esta data.
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Container>

      {isOngoingEvent && ongoingEvents.length > 0 && (
        <GlobalModal
          title="Eventos em andamento"
          width={isMobile ? '95%' : '50rem'}
          height={isMobile ? '70%' : 'auto'}
          open={isOngoingEvent}
          onClose={() => setIsOngoingEvent(false)}
          onCancel={() => setIsOngoingEvent(false)}
          cancelButtonText="Fechar"
          widthButtons={isMobile ? 'auto' : '40%'}
          closeOnEscapeKeyDown
          closeOnBackdropClick
        >
          <Stack direction="column" spacing={2}>
            {ongoingEvents.map((event: Event) => (
              <OngoingEvent
                key={event.id}
                event={event}
                onDetails={() => handleEventDetails(event)}
                onAccess={() => handleAccessMeeting(event)}
              />
            ))}
          </Stack>
        </GlobalModal>
      )}

      {/* Modal de detalhes do evento */}
      {selectedEvent && (
        <DetailsModal
          open={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          onConfirmOccurrence={() => {}}
          onConfirmSeries={() => {}}
          event={selectedEvent}
          correspondingEvent={
            {
              ...selectedEvent,
              description: selectedEvent.description,
              type: {
                ...selectedEvent.type,
                is_private: selectedEvent.type.isPrivate,
              },
              is_exclusive: false,
              schedule: {
                rule: {
                  id: selectedEvent.id,
                  event_id: selectedEvent.id,
                  init_date: selectedEvent.initDatetime.split('T')[0],
                  init_hour: selectedEvent.initDatetime.split('T')[1]?.split('.')[0] || '00:00:00',
                  duration: parseInt(selectedEvent.duration) || 60,
                  is_recurrent: false,
                  recurrence_frequency: 'DAILY' as const,
                  recurrence_count: 1,
                  recurrence_by_week: [],
                  recurrence_by_month: '',
                  recurrence_end_rule: 'AFTER_N_OCCURRENCES' as const,
                  recurrence_rule_limit: 1,
                  recurrence_end_date: undefined,
                  recurrence_end_time: undefined,
                },
              },
            } as any
          }
          eventStatus={selectedEvent.status}
          isPrivateMeeting={selectedEvent.type.isPrivate}
        />
      )}

      {/* Modal de localização para eventos presenciais */}
      {selectedEvent && !selectedEvent.isOnline && (
        <GlobalModal
          title="Localização do Evento"
          width={isMobile ? '95%' : '40rem'}
          height={isMobile ? '60%' : 'auto'}
          open={isLocationModalOpen}
          onClose={handleCloseLocationModal}
          onCancel={handleCloseLocationModal}
          cancelButtonText="Fechar"
          widthButtons={isMobile ? '100%' : '50%'}
          closeOnEscapeKeyDown
          closeOnBackdropClick
        >
          <Box sx={{ padding: 2 }}>
            <Stack spacing={2}>
              <Box>
                <Text size="medium" bold color="primary">
                  {selectedEvent.title}
                </Text>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <LocationOn color="primary" />
                <Text size="medium" bold>
                  Local:
                </Text>
              </Box>

              <Box sx={{ paddingLeft: 4 }}>
                <Text size="medium">{selectedEvent.location}</Text>
              </Box>

              <Box>
                <Text size="medium" bold color="primary">
                  Data e Horário:
                </Text>
                <Text size="medium">
                  {selectedEvent.date} às {selectedEvent.time}
                </Text>
              </Box>

              <Box>
                <Text size="medium" bold color="primary">
                  Duração:
                </Text>
                <Text size="medium">{selectedEvent.duration}</Text>
              </Box>

              {selectedEvent.coordinator && (
                <Box>
                  <Text size="medium" bold color="primary">
                    Coordenador:
                  </Text>
                  <Text size="medium">
                    {selectedEvent.coordinator.firstName} {selectedEvent.coordinator.lastName}
                  </Text>
                </Box>
              )}
            </Stack>
          </Box>
        </GlobalModal>
      )}
    </>
  );
};

export default AgendaPage;
